import { useGetExternalAccountsSuspense } from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewLayout,
  PaymentViewLoading,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useCallback, useState } from 'react';
import { makePath } from 'utils/routes';

import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';

import { CreateExternalAccountModal } from './CreateExternalAccountModal';

type AccountRow = {
  id: string;
  external_id: string;
  name: string;
};

const accountSchema: ColumnDefinition<AccountRow>[] = [
  {
    title: 'Name',
    key: 'name',
    colspan: 'col-span-4',
  },
  {
    title: 'External ID',
    key: 'external_id',
    colspan: 'col-span-2',
  },
];

const accountEmptyValue = {
  Name: 'No Accounts Yet',
  'External ID': 'N/A',
};

type Props = {
  workspaceId: string;
};

const TransactionListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Recon');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const { data: accountsData } = useGetExternalAccountsSuspense();

  const accounts: AccountRow[] = accountsData.data;

  const getPath = useCallback(
    (account: AccountRow) =>
      makePath(['w', workspaceId, 'payments', 'accounts', account.id], {
        absolute: true,
      }),
    [workspaceId]
  );

  const handleCreate = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  return (
    <>
      <PaymentViewLayout
        title="Accounts"
        primaryAction={{
          title: 'New',
          onClick: handleCreate,
          'data-testid': 'create-external-account-button',
        }}
      >
        <div className="flex flex-col min-h-f1 w-full h-full">
          <NavTableV2
            textSize="md"
            data={accounts}
            emptyValue={accountEmptyValue}
            getPath={getPath}
            schema={accountSchema}
            showHeader={false}
            stickyHeader={false}
          />
        </div>
      </PaymentViewLayout>
      {showCreateModal && (
        <CreateExternalAccountModal onClose={handleCloseModal} />
      )}
    </>
  );
};

export const TransactionListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<PaymentViewLoading message="Loading recon..." />}>
    <TransactionListPageContent workspaceId={workspaceId} />
  </Suspense>
);
